import type { ReactNode } from 'react';

interface RepoSummary {
  fullName: string;
  org: string;
  repo: string;
  indexedAt?: string;
  registeredAt?: string;
  nodeCount?: number;
}

interface RepoRowProps {
  repo: RepoSummary;
  isStarred: boolean;
  lastSearchCount?: number;
  onStar: (e: React.MouseEvent) => void;
  onReindex: (e: React.MouseEvent) => void;
  reindexing: boolean;
  reindexFailed: boolean;
}

/** Returns a human-readable relative time string for a given ISO 8601 timestamp. */
function formatRelativeTime(isoString: string | undefined): string | null {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return null;

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 1) return 'just now';

  const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round(
    (nowMidnight.getTime() - dateMidnight.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays <= 6) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Returns a formatted date string for a registeredAt ISO 8601 timestamp.
 *  Returns null for undefined/unparseable input.
 *  Returns "—" for dates before 2020-01-01 (e.g. DateTimeOffset.MinValue sentinel). */
export function formatRegisteredAt(value: string | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  if (d.getFullYear() < 2020) return '—';
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
}

/**
 * S-UI-002: Dense row replacing the old .indexed-repo-card grid card.
 *
 * Layout (desktop):
 *   [star] org/repo name       symbol count  |  time ago  [->] [re-index]
 *
 * Layout (mobile, ≤600px):
 *   [star] org/repo name
 *   symbol count  |  time ago  [->] Search
 *
 * Action buttons (NavigateButton, ReindexButton) are CSS opacity-reveal on
 * desktop (opacity: 0 at rest; revealed on row hover/focus-within). On mobile
 * the navigate button is always visible; re-index is omitted for now.
 *
 * Root element uses data-testid="repo-row" for Playwright selectors.
 */
export function RepoRow({
  repo,
  isStarred,
  lastSearchCount,
  onStar,
  onReindex,
  reindexing,
  reindexFailed,
}: RepoRowProps): ReactNode {
  const relativeTime = formatRelativeTime(repo.indexedAt);
  const registeredAtFormatted = formatRegisteredAt(repo.registeredAt);

  return (
    <div
      className={`repo-row${isStarred ? ' repo-row--starred' : ''}`}
      data-testid="repo-row"
      role="listitem"
    >
      {/* Star/pin toggle */}
      <button
        type="button"
        className={`repo-row__star star-btn${isStarred ? ' starred' : ''}`}
        data-testid={`star-btn-${repo.fullName.replace('/', '-')}`}
        onClick={onStar}
        aria-label={isStarred ? `Unpin ${repo.fullName}` : `Pin ${repo.fullName}`}
        aria-pressed={isStarred}
        title={isStarred ? 'Remove from favorites' : 'Add to favorites'}
      >
        {isStarred ? (
          /* Filled star SVG */
          <svg
            viewBox="0 0 16 16"
            width="14"
            height="14"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.751.751 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25Z" />
          </svg>
        ) : (
          /* Outline star SVG */
          <svg
            viewBox="0 0 16 16"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.751.751 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25Z" />
          </svg>
        )}
      </button>

      {/* Repo name — clicking navigates to /search?repo={fullName} */}
      <a
        href={`/search?repo=${encodeURIComponent(repo.fullName)}`}
        className="repo-row__name"
        tabIndex={0}
      >
        {repo.fullName}
      </a>

      {/* Meta — symbol count + indexed time + last search badge */}
      <span className="repo-row__meta">
        {repo.nodeCount !== undefined && (
          <span
            data-testid="repo-card-node-count"
            className="repo-row__meta-count"
            aria-label={`${repo.nodeCount.toLocaleString()} symbols indexed`}
          >
            {repo.nodeCount.toLocaleString()} symbols
          </span>
        )}
        {repo.nodeCount !== undefined && relativeTime && (
          <span className="repo-row__meta-sep" aria-hidden="true">|</span>
        )}
        {relativeTime && (
          <span data-testid="repo-card-indexed-at" className="repo-row__meta-time">
            {relativeTime}
          </span>
        )}
        {registeredAtFormatted !== null && relativeTime === null && (
          <time
            dateTime={repo.registeredAt}
            data-testid="repo-card-registered-at"
            className="repo-row__meta-registered"
          >
            Registered: {registeredAtFormatted}
          </time>
        )}
        {(lastSearchCount ?? 0) > 0 && (
          <>
            <span className="repo-row__meta-sep" aria-hidden="true">|</span>
            <span
              data-testid={`result-count-badge-${repo.fullName.replace('/', '-')}`}
              className="repo-row__meta-last-search"
            >
              last search: {lastSearchCount} results
            </span>
          </>
        )}
      </span>

      {/* Action buttons — CSS opacity-reveal on desktop; always visible on mobile */}
      <div className="repo-row__actions">
        {/* Navigate to search scoped to this repo */}
        <a
          href={`/search?repo=${encodeURIComponent(repo.fullName)}`}
          className="repo-row__action repo-row__action--navigate search-repo-btn"
          data-testid={`search-repo-btn-${repo.fullName.replace('/', '-')}`}
          aria-label={`Search ${repo.fullName}`}
          onClick={(e) => e.stopPropagation()}
        >
          →
        </a>

        {/* Re-index button */}
        <button
          type="button"
          className="repo-row__action repo-row__action--reindex reindex-btn"
          data-testid={`reindex-btn-${repo.fullName.replace('/', '-')}`}
          aria-label={`Re-index ${repo.fullName}`}
          disabled={reindexing}
          onClick={onReindex}
        >
          {reindexing ? '…' : reindexFailed ? '!' : '↻'}
        </button>
      </div>
    </div>
  );
}
